import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import {
  getDownloadErrorMessage,
  saveBlobResponse,
} from '../../../application/utils/downloadFile';
import { EventApiService } from '../../services/event-api/EventApiService';
import {
  EventParticipant,
  EventTicket,
  SaveTicketData,
  SportsEvent,
  Team,
  TicketStatus,
} from '../../types/Event';
import { resolveTeamLogoBackground } from '../../utils/resolveTeamLogoUrl';

interface BulkTicketsParseResult {
  tickets: SaveTicketData[];
  errors: string[];
}

interface TicketPriceDraft {
  purchasePrice: number;
  salePrice: number;
  purchasePaidByUserId: string | null;
}

interface TicketSeatItem {
  id: string;
  seat: string;
  status: TicketStatus;
  purchasePrice: number;
  salePrice: number;
  startsNewGroup: boolean;
}

interface TicketSeatRun {
  label: string;
  count: number;
}

interface TicketSeatRowGroup {
  row: string;
  seats: TicketSeatItem[];
  availableRuns: TicketSeatRun[];
}

interface TicketSeatSectorGroup {
  sector: string;
  rows: TicketSeatRowGroup[];
}

interface EventSalesInsight {
  tone: 'neutral' | 'success' | 'warning' | 'danger';
  title: string;
  description: string;
}

type TicketSortField = 'code' | 'sector' | 'row' | 'seat' | 'purchase' | 'sale' | 'payer' | 'status';
type SortDirection = 'asc' | 'desc';

@Component({
  selector: 'app-event-view-page',
  standalone: false,
  templateUrl: './event-view-page.component.html',
  styleUrl: './event-view-page.component.sass',
})
export class EventViewPageComponent implements OnInit {
  private readonly naturalCollator = new Intl.Collator('ru-RU', {
    numeric: true,
    sensitivity: 'base',
  });
  private readonly dayMs = 24 * 60 * 60 * 1000;

  public event: SportsEvent | null = null;
  public ticketForm: SaveTicketData = this.createEmptyTicketForm();
  public ticketPriceDrafts: Record<string, TicketPriceDraft> = {};
  public selectedTicketIds: string[] = [];
  public bulkTicketsText = '';
  public bulkPurchasePaidByUserId: string | null = null;
  public bulkSalePrice: number | null = null;
  public loading = false;
  public savingTicket = false;
  public bulkAddingTickets = false;
  public applyingBulkSalePrice = false;
  public savingTicketPrices: Record<string, boolean> = {};
  public downloadingTicketFiles: Record<string, boolean> = {};
  public errorMessage: string | null = null;
  public successMessage: string | null = null;
  public ticketSortField: TicketSortField = 'sector';
  public ticketSortDirection: SortDirection = 'asc';

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventApi: EventApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    const eventId = this.route.snapshot.paramMap.get('eventId');
    if (!eventId) {
      this.errorMessage = 'Событие не найдено.';
      return;
    }

    await this.loadEvent(eventId);
  }

  public get ticketsMode(): boolean {
    return this.router.url.startsWith('/dashboard/tickets');
  }

  public get ticketAddMode(): boolean {
    return this.router.url.startsWith('/dashboard/tickets/') && this.router.url.endsWith('/add');
  }

  public get canOpenTickets(): boolean {
    return this.event?.status === 'in_progress';
  }

  public get eventStoragePath(): string {
    if (!this.event?.storagePrefix) {
      return 'Не настроено';
    }

    return `${this.event.storagePrefix}/`;
  }

  public get sortedTickets(): EventTicket[] {
    if (!this.event) {
      return [];
    }

    const direction = this.ticketSortDirection === 'asc' ? 1 : -1;

    return [
      ...this.event.tickets,
    ].sort((left, right) => (
      this.compareTicketByField(left, right, this.ticketSortField) * direction
      || this.compareTicketPosition(left, right)
      || this.compareNatural(left.code, right.code)
    ));
  }

  public setTicketSort(field: TicketSortField): void {
    if (this.ticketSortField === field) {
      this.ticketSortDirection = this.ticketSortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.ticketSortField = field;
    this.ticketSortDirection = field === 'purchase' || field === 'sale' ? 'desc' : 'asc';
  }

  public ticketSortState(field: TicketSortField): string {
    if (this.ticketSortField !== field) {
      return 'none';
    }

    return this.ticketSortDirection === 'asc' ? 'ascending' : 'descending';
  }

  public ticketSortMark(field: TicketSortField): string {
    if (this.ticketSortField !== field) {
      return '↕';
    }

    return this.ticketSortDirection === 'asc' ? '↑' : '↓';
  }

  public get soldTicketsCount(): number {
    return this.countTicketsByStatus('sold');
  }

  public get remainingTicketsCount(): number {
    if (!this.event) {
      return 0;
    }

    return Math.max(this.event.summary.ticketsTotal - this.soldTicketsCount, 0);
  }

  public get soldRevenueTotal(): number {
    return this.event?.summary.soldRevenue ?? 0;
  }

  public get breakEvenTarget(): number {
    return this.event?.summary.totalCost ?? 0;
  }

  public get breakEvenRemaining(): number {
    return Math.max(this.breakEvenTarget - this.soldRevenueTotal, 0);
  }

  public get breakEvenProgressPercent(): number {
    if (this.breakEvenTarget <= 0) {
      return this.soldRevenueTotal > 0 ? 100 : 0;
    }

    return Math.min(Math.max((this.soldRevenueTotal / this.breakEvenTarget) * 100, 0), 100);
  }

  public get breakEvenProgressStyle(): string {
    return `${this.breakEvenProgressPercent.toFixed(1)}%`;
  }

  public get isBreakEvenReached(): boolean {
    return this.breakEvenTarget > 0 && this.soldRevenueTotal >= this.breakEvenTarget;
  }

  public get breakEvenStatusText(): string {
    if (this.breakEvenTarget <= 0) {
      return 'Затраты пока не сформированы.';
    }

    if (this.isBreakEvenReached) {
      return 'Событие окупилось, следующие продажи идут в плюс.';
    }

    return `До выхода в ноль осталось ${this.formatMoney(this.breakEvenRemaining)}.`;
  }

  public get daysUntilEvent(): number {
    if (!this.event) {
      return 0;
    }

    const today = this.stripTime(new Date());
    const eventDate = this.stripTime(new Date(this.event.startsAt));

    return Math.max(Math.ceil((eventDate.getTime() - today.getTime()) / this.dayMs), 0);
  }

  public get daysSinceTicketsStart(): number {
    if (!this.event || this.event.tickets.length === 0) {
      return 0;
    }

    const ticketTimestamps = this.event.tickets
      .map((ticket) => new Date(ticket.createdAt).getTime())
      .filter((timestamp) => Number.isFinite(timestamp));
    if (ticketTimestamps.length === 0) {
      return 1;
    }

    const firstTicketDate = this.stripTime(new Date(Math.min(...ticketTimestamps)));
    const today = this.stripTime(new Date());

    return Math.max(Math.floor((today.getTime() - firstTicketDate.getTime()) / this.dayMs) + 1, 1);
  }

  public get averageSoldPerDay(): number {
    if (this.daysSinceTicketsStart <= 0) {
      return 0;
    }

    return this.soldTicketsCount / this.daysSinceTicketsStart;
  }

  public get requiredSalesPerDay(): number {
    if (this.remainingTicketsCount <= 0) {
      return 0;
    }

    return this.remainingTicketsCount / Math.max(this.daysUntilEvent, 1);
  }

  public get salesInsight(): EventSalesInsight {
    if (!this.event || this.event.summary.ticketsTotal === 0) {
      return {
        tone: 'neutral',
        title: 'Билеты не добавлены',
        description: 'После добавления билетов здесь появится темп продаж и ориентир по плану.',
      };
    }

    if (this.remainingTicketsCount === 0) {
      return {
        tone: 'success',
        title: 'Все билеты проданы',
        description: 'По этому событию больше не осталось билетов для продажи.',
      };
    }

    if (this.daysUntilEvent === 0) {
      return {
        tone: 'danger',
        title: 'Событие уже близко',
        description: `Осталось ${this.remainingTicketsCount.toString()} билетов. Нужна быстрая ручная проверка цены и спроса.`,
      };
    }

    if (this.soldTicketsCount === 0 && this.daysSinceTicketsStart >= 2) {
      return {
        tone: 'danger',
        title: 'Продаж пока нет',
        description: `До события ${this.daysUntilEvent.toString()} дн., нужно продавать примерно ${this.formatTicketsPerDay(this.requiredSalesPerDay)}.`,
      };
    }

    if (this.soldTicketsCount > 0 && this.averageSoldPerDay >= this.requiredSalesPerDay * 1.25) {
      return {
        tone: 'success',
        title: 'Спрос выше плана',
        description: 'Текущий темп продаж опережает равномерный план. Можно аккуратно проверять повышение цены.',
      };
    }

    if (this.averageSoldPerDay < this.requiredSalesPerDay * 0.7) {
      return {
        tone: 'warning',
        title: 'Спрос ниже плана',
        description: 'Темп продаж отстает от равномерного плана. Стоит проверить цену, объявления и рассылку.',
      };
    }

    return {
      tone: 'neutral',
      title: 'Темп в норме',
      description: 'Продажи идут около равномерного плана до даты события.',
    };
  }

  public async addTicket(): Promise<void> {
    if (!this.event || !this.ticketForm.code.trim()) {
      return;
    }

    const purchasePaidByUserId = this.normalizePurchasePayer(this.ticketForm.purchasePaidByUserId);
    if (this.event.participants.length > 0 && !purchasePaidByUserId) {
      this.errorMessage = 'Укажите, кто оплатил закупку билета.';
      return;
    }

    this.savingTicket = true;
    this.errorMessage = null;

    try {
      const response = await this.eventApi.createTicket(this.event.slug || this.event.id, {
        ...this.ticketForm,
        purchasePaidByUserId,
      });
      const event = this.unwrapResponse(response);
      if (event) {
        this.setEvent(event);
        this.ticketForm = this.createEmptyTicketForm(this.getDefaultPurchasePayerId(event));
        this.successMessage = 'Билет добавлен.';
        this.notificationService.notify('event', 'Билет добавлен', `${event.homeTeam.name} - ${event.awayTeam.name}`);
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.savingTicket = false;
      this.changeDetector.detectChanges();
    }
  }

  public async addBulkTickets(): Promise<void> {
    if (!this.event || !this.bulkTicketsText.trim()) {
      return;
    }

    const parsedTickets = this.parseBulkTickets(this.bulkTicketsText);
    this.successMessage = null;
    if (parsedTickets.errors.length > 0) {
      this.errorMessage = parsedTickets.errors.join('\n');
      return;
    }

    const purchasePaidByUserId = this.normalizePurchasePayer(this.bulkPurchasePaidByUserId);
    if (this.event.participants.length > 0 && !purchasePaidByUserId) {
      this.errorMessage = 'Укажите, кто оплатил закупку билетов.';
      return;
    }

    this.bulkAddingTickets = true;
    this.errorMessage = null;

    try {
      const response = await this.eventApi.createTicketsBulk(this.event.slug || this.event.id, parsedTickets.tickets.map((ticket) => ({
        ...ticket,
        purchasePaidByUserId,
      })));
      const result = this.unwrapResponse(response);
      if (result) {
        if (result.event) {
          this.setEvent(result.event);
        }

        this.successMessage = `Добавлено билетов: ${result.created.toString()}.`;
        this.errorMessage = result.errors.length > 0 ? result.errors.join('\n') : null;
        if (result.errors.length === 0) {
          this.bulkTicketsText = '';
        }

        this.notificationService.notify('event', 'Билеты добавлены', `${this.event.homeTeam.name} - ${this.event.awayTeam.name}: ${result.created.toString()}`);
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.bulkAddingTickets = false;
      this.changeDetector.detectChanges();
    }
  }

  public async updateTicketStatus(ticketId: string, status: TicketStatus): Promise<void> {
    if (!this.event) {
      return;
    }
    const ticket = this.event.tickets.find((eventTicket) => eventTicket.id === ticketId);
    if (ticket && !this.canEditTicketStatus(ticket)) {
      this.errorMessage = this.getTicketStatusControlTitle(ticket);
      this.changeDetector.detectChanges();
      return;
    }

    try {
      const response = await this.eventApi.updateTicket(this.event.slug || this.event.id, ticketId, { status });
      const event = this.unwrapResponse(response);
      if (event) {
        this.setEvent(event);
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public isTicketSelected(ticketId: string): boolean {
    return this.selectedTicketIds.includes(ticketId);
  }

  public toggleTicketSelection(ticketId: string, selected: boolean): void {
    const ticket = this.event?.tickets.find((eventTicket) => eventTicket.id === ticketId);
    if (selected && ticket && !this.canEditTicketSalePrice(ticket)) {
      return;
    }

    this.selectedTicketIds = selected
      ? [
        ...new Set([
          ...this.selectedTicketIds,
          ticketId,
        ]),
      ]
      : this.selectedTicketIds.filter((selectedTicketId) => selectedTicketId !== ticketId);
  }

  public toggleAllTickets(selected: boolean): void {
    if (!this.event) {
      return;
    }

    this.selectedTicketIds = selected
      ? this.event.tickets.filter((ticket) => this.canEditTicketSalePrice(ticket)).map((ticket) => ticket.id)
      : [];
  }

  public get selectedTicketsCount(): number {
    return this.selectedTicketIds.length;
  }

  public get allTicketsSelected(): boolean {
    if (!this.event || this.event.tickets.length === 0) {
      return false;
    }

    const editableTickets = this.event.tickets.filter((ticket) => this.canEditTicketSalePrice(ticket));
    return editableTickets.length > 0 && editableTickets.every((ticket) => this.selectedTicketIds.includes(ticket.id));
  }

  public isTicketPriceChanged(ticket: EventTicket): boolean {
    const draft = this.ticketPriceDrafts[ticket.id];

    return !!draft
      && (draft.purchasePrice !== ticket.purchasePrice
        || (this.canEditTicketSalePrice(ticket) && draft.salePrice !== ticket.salePrice)
        || draft.purchasePaidByUserId !== ticket.purchasePaidByUserId);
  }

  public async saveTicketPrices(ticket: EventTicket): Promise<void> {
    if (!this.event) {
      return;
    }

    const draft = this.ticketPriceDrafts[ticket.id];
    if (!draft) {
      return;
    }

    const data: Partial<SaveTicketData> = {};
    const purchasePrice = this.validatePrice(draft.purchasePrice);
    if (purchasePrice === null) {
      this.errorMessage = 'Цена закупки не может быть отрицательной.';
      return;
    }
    data.purchasePrice = purchasePrice;

    if (this.canEditTicketSalePrice(ticket)) {
      const salePrice = this.validatePrice(draft.salePrice);
      if (salePrice === null) {
        this.errorMessage = 'Цена продажи не может быть отрицательной.';
        return;
      }
      data.salePrice = salePrice;
    }
    const purchasePaidByUserId = this.normalizePurchasePayer(draft.purchasePaidByUserId);
    if (this.event.participants.length > 0 && !purchasePaidByUserId) {
      this.errorMessage = 'Укажите, кто оплатил закупку билета.';
      return;
    }
    data.purchasePaidByUserId = purchasePaidByUserId;

    this.savingTicketPrices = {
      ...this.savingTicketPrices,
      [ticket.id]: true,
    };
    this.errorMessage = null;

    try {
      const response = await this.eventApi.updateTicket(this.event.slug || this.event.id, ticket.id, data);
      const event = this.unwrapResponse(response);
      if (event) {
        this.setEvent(event);
        this.successMessage = `Параметры билета ${ticket.code} обновлены.`;
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.savingTicketPrices = {
        ...this.savingTicketPrices,
        [ticket.id]: false,
      };
      this.changeDetector.detectChanges();
    }
  }

  public async applyBulkSalePrice(): Promise<void> {
    if (!this.event || this.selectedTicketIds.length === 0) {
      return;
    }

    const salePrice = this.validatePrice(this.bulkSalePrice);
    if (salePrice === null) {
      this.errorMessage = 'Укажите корректную цену продажи.';
      return;
    }

    const eventId = this.event.slug || this.event.id;
    const ticketsById = new Map(this.event.tickets.map((ticket) => [
      ticket.id,
      ticket,
    ]));
    const editableSelectedTicketIds = this.selectedTicketIds.filter((ticketId) => {
      const ticket = ticketsById.get(ticketId);
      return ticket ? this.canEditTicketSalePrice(ticket) : false;
    });
    if (editableSelectedTicketIds.length === 0) {
      this.errorMessage = 'Среди выбранных билетов нет доступных для изменения цены.';
      return;
    }
    let updatedEvent: SportsEvent | null = null;
    let updatedTickets = 0;
    const errors: string[] = [];

    this.applyingBulkSalePrice = true;
    this.errorMessage = null;
    this.successMessage = null;

    try {
      for (const ticketId of editableSelectedTicketIds) {
        const ticket = ticketsById.get(ticketId);
        if (!ticket) {
          continue;
        }

        const response = await this.eventApi.updateTicket(eventId, ticketId, { salePrice });
        if (response.status === 'failed') {
          errors.push(`${ticket.code}: ${response.errorMessage}`);
        } else {
          updatedEvent = response.response;
          updatedTickets += 1;
        }
      }

      if (updatedEvent) {
        this.setEvent(updatedEvent);
      }

      this.successMessage = `Обновлено билетов: ${updatedTickets.toString()}.`;
      this.errorMessage = errors.length > 0 ? errors.join('\n') : null;
      this.notificationService.notify('event', 'Цена продажи обновлена', `Билетов: ${updatedTickets.toString()}`);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.applyingBulkSalePrice = false;
      this.changeDetector.detectChanges();
    }
  }

  public async deleteTicket(ticketId: string): Promise<void> {
    if (!this.event || !window.confirm('Удалить билет из события?')) {
      return;
    }

    try {
      const response = await this.eventApi.deleteTicket(this.event.slug || this.event.id, ticketId);
      const event = this.unwrapResponse(response);
      if (event) {
        this.setEvent(event);
        this.successMessage = 'Билет удален.';
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public canDownloadTicket(ticket: EventTicket): boolean {
    return (ticket.storageFilesCount ?? 0) > 0;
  }

  public canEditTicketStatus(ticket: EventTicket): boolean {
    return !ticket.reservationOrderId && !ticket.soldOrderId;
  }

  public canEditTicketSalePrice(ticket: EventTicket): boolean {
    return !ticket.soldOrderId;
  }

  public getTicketStatusControlTitle(ticket: EventTicket): string {
    if (ticket.reservationOrderId) {
      return 'Билет находится в заказе-резерве. Статус меняется через заказ.';
    }
    if (ticket.soldOrderId) {
      return 'Билет уже продан через заказ. Статус оплаченного билета нельзя менять вручную.';
    }

    return 'Изменить статус билета';
  }

  public async downloadTicketFile(ticket: EventTicket): Promise<void> {
    if (!this.event || !this.canDownloadTicket(ticket)) {
      return;
    }

    this.downloadingTicketFiles = {
      ...this.downloadingTicketFiles,
      [ticket.id]: true,
    };
    this.errorMessage = null;

    try {
      const response = await this.eventApi.downloadTicketFile(this.event.slug || this.event.id, ticket.id);
      saveBlobResponse(response, `${ticket.code}.pdf`);
    } catch (error) {
      this.errorMessage = await getDownloadErrorMessage(error, `Не удалось скачать файл билета ${ticket.code}.`);
    } finally {
      this.downloadingTicketFiles = {
        ...this.downloadingTicketFiles,
        [ticket.id]: false,
      };
      this.changeDetector.detectChanges();
    }
  }

  public async deleteEvent(): Promise<void> {
    if (!this.event || !window.confirm(`Удалить событие ${this.event.homeTeam.name} - ${this.event.awayTeam.name}?`)) {
      return;
    }

    try {
      const response = await this.eventApi.deleteEvent(this.event.slug || this.event.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      await this.router.navigate([
        '/dashboard/events',
      ]);
    } catch (error) {
      this.handleUnknownError(error);
    }
  }

  public getSeasonName(event: SportsEvent): string {
    const seasonType = event.seasonType === 'playoff' ? 'Плей-офф' : 'Регулярный сезон';
    return `${event.seasonName}, ${seasonType}${event.playoffStage ? `, ${event.playoffStage}` : ''}`;
  }

  public getEventStatusName(status: SportsEvent['status']): string {
    if (status === 'completed') {
      return 'Завершен';
    }
    if (status === 'in_progress') {
      return 'В работе';
    }

    return 'Планируется';
  }

  public getTicketStatusName(status: TicketStatus): string {
    if (status === 'reserved') {
      return 'Резерв';
    }
    if (status === 'sold') {
      return 'Выкуплен';
    }

    return 'В наличии';
  }

  public formatDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public formatNumber(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      maximumFractionDigits: 1,
    }).format(value);
  }

  public formatTicketsPerDay(value: number): string {
    return `${this.formatNumber(value)} бил./день`;
  }

  public formatRoi(event: SportsEvent): string {
    if (event.summary.totalCost <= 0) {
      return '0%';
    }

    return new Intl.NumberFormat('ru-RU', {
      style: 'percent',
      maximumFractionDigits: 1,
    }).format(event.summary.plannedProfitAfterExpenses / event.summary.totalCost);
  }

  public getExpenseScopeName(scope: 'event' | 'monthly'): string {
    return scope === 'monthly' ? 'Месячный' : 'На событие';
  }

  public getParticipantName(participant: EventParticipant): string {
    return participant.displayName || participant.username;
  }

  public getPurchasePayerName(userId: string | null | undefined): string {
    if (!userId) {
      return 'Не указан';
    }

    const participant = this.event?.participants.find((eventParticipant) => eventParticipant.userId === userId);
    return participant ? this.getParticipantName(participant) : 'Вне участников';
  }

  public getTicketStorageFileTitle(ticket: EventTicket): string {
    const filesCount = ticket.storageFilesCount ?? 0;
    if (ticket.storageFileStatus === 'ready') {
      return filesCount === 1 ? 'Файл билета загружен.' : `Файлов билета загружено: ${filesCount.toString()}.`;
    }
    if (ticket.storageFileStatus === 'mismatch') {
      return 'Файл найден, но лежит не в папке текущего статуса билета.';
    }

    return 'Файл билета не найден в MinIO.';
  }

  public isEventParticipant(userId: string | null | undefined): boolean {
    return !!userId && !!this.event?.participants.some((participant) => participant.userId === userId);
  }

  public get ticketSeatGroups(): TicketSeatSectorGroup[] {
    if (!this.event) {
      return [];
    }

    const sectorRows = new Map<string, Map<string, EventTicket[]>>();
    for (const ticket of this.event.tickets) {
      if (ticket.status === 'sold') {
        continue;
      }

      let rows = sectorRows.get(ticket.sector);
      if (!rows) {
        rows = new Map<string, EventTicket[]>();
        sectorRows.set(ticket.sector, rows);
      }

      rows.set(ticket.row, [
        ...(rows.get(ticket.row) ?? []),
        ticket,
      ]);
    }

    return [
      ...sectorRows.entries(),
    ].sort((firstEntry, secondEntry) => this.compareNatural(firstEntry[0], secondEntry[0])).map((sectorEntry) => {
      const [
        sector,
        rows,
      ] = sectorEntry;

      return {
        sector,
        rows: [
          ...rows.entries(),
        ].sort((firstEntry, secondEntry) => this.compareNatural(firstEntry[0], secondEntry[0])).map((rowEntry) => {
          const [
            row,
            tickets,
          ] = rowEntry;

          return this.createSeatRowGroup(row, tickets);
        }),
      };
    });
  }

  public getLogoBackground(team: Team): string | null {
    return resolveTeamLogoBackground(team.logoUrl);
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  public trackBySector(_index: number, item: TicketSeatSectorGroup): string {
    return item.sector;
  }

  public trackByRow(_index: number, item: TicketSeatRowGroup): string {
    return item.row;
  }

  public trackBySeatRun(_index: number, item: TicketSeatRun): string {
    return item.label;
  }

  public trackByUserId(_index: number, item: EventParticipant): string {
    return item.userId;
  }

  private async loadEvent(eventId: string): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const response = await this.eventApi.getEvent(eventId);
      const event = this.unwrapResponse(response);
      if (!event) {
        this.errorMessage = 'Событие не найдено.';
        return;
      }

      this.setEvent(event);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private createEmptyTicketForm(purchasePaidByUserId: string | null = null): SaveTicketData {
    return {
      code: '',
      purchasePrice: 0,
      salePrice: 0,
      status: 'available',
      purchasePaidByUserId,
    };
  }

  private parseBulkTickets(source: string): BulkTicketsParseResult {
    const tickets: SaveTicketData[] = [];
    const errors: string[] = [];
    const lines = source.split(/\r?\n/).map((line) => line.trim()).filter((line) => line.length > 0);

    for (const [
      index,
      line,
    ] of lines.entries()) {
      const ticket = this.parseBulkTicketLine(line);
      if (ticket) {
        tickets.push(ticket);
      } else {
        errors.push(`Строка ${String(index + 1)}: не удалось распознать билет.`);
      }
    }

    if (tickets.length === 0 && errors.length === 0) {
      errors.push('Добавьте хотя бы одну строку с билетом.');
    }

    return {
      tickets,
      errors,
    };
  }

  private parseBulkTicketLine(line: string): SaveTicketData | null {
    const textMatch = /^Сектор\s+(.+?)\s+Ряд\s+(.+?)\s+Место\s+(.+?)\s*[-–—]\s*Цена\s+(.+?)\s*(?:₽|Р|руб\.?)?$/i.exec(line);
    if (textMatch) {
      return this.createTicketFromParts(textMatch[1], textMatch[2], textMatch[3], textMatch[4]);
    }

    const compactMatch = /^(.+?)-(.+?)-(.+?)-(.+)$/.exec(line);
    if (compactMatch) {
      return this.createTicketFromParts(compactMatch[1], compactMatch[2], compactMatch[3], compactMatch[4]);
    }

    return null;
  }

  private createTicketFromParts(sector: string, row: string, seat: string, priceValue: string): SaveTicketData | null {
    const codeParts = [
      sector,
      row,
      seat,
    ].map((value) => value.trim()).filter((value) => value.length > 0);
    const purchasePrice = this.parsePrice(priceValue);
    if (codeParts.length !== 3 || purchasePrice === null) {
      return null;
    }

    return {
      code: codeParts.join('-'),
      purchasePrice,
      salePrice: 0,
      status: 'available',
    };
  }

  private parsePrice(value: string): number | null {
    const normalizedValue = value.replace(/\s/g, '').replace(',', '.').replace(/[^\d.]/g, '');
    const price = Number(normalizedValue);
    return Number.isFinite(price) && price >= 0 ? price : null;
  }

  private validatePrice(value: number | null): number | null {
    const price = Number(value);
    return Number.isFinite(price) && price >= 0 ? price : null;
  }

  private createSeatRowGroup(row: string, tickets: EventTicket[]): TicketSeatRowGroup {
    const sortedTickets = [
      ...tickets,
    ].sort((firstTicket, secondTicket) => this.compareNatural(firstTicket.seat, secondTicket.seat));
    const seats = sortedTickets.map((ticket, index) => {
      const seatNumber = this.parseSeatNumber(ticket.seat);
      const previousSeatNumber = index === 0 ? null : this.parseSeatNumber(sortedTickets[index - 1].seat);

      return {
        id: ticket.id,
        seat: ticket.seat,
        status: ticket.status,
        purchasePrice: ticket.purchasePrice,
        salePrice: ticket.salePrice,
        startsNewGroup: index > 0 && (seatNumber === null || previousSeatNumber === null || seatNumber !== previousSeatNumber + 1),
      };
    });

    return {
      row,
      seats,
      availableRuns: this.createAvailableSeatRuns(seats),
    };
  }

  private createAvailableSeatRuns(seats: TicketSeatItem[]): TicketSeatRun[] {
    const runs: TicketSeatRun[] = [];
    let currentRun: TicketSeatItem[] = [];

    const flushRun = (): void => {
      if (currentRun.length === 0) {
        return;
      }

      const firstSeat = currentRun[0];
      const lastSeat = currentRun[currentRun.length - 1];
      runs.push({
        label: currentRun.length === 1 ? firstSeat.seat : `${firstSeat.seat}-${lastSeat.seat}`,
        count: currentRun.length,
      });
      currentRun = [];
    };

    for (const seat of seats.filter((seatItem) => seatItem.status === 'available')) {
      const seatNumber = this.parseSeatNumber(seat.seat);
      if (currentRun.length === 0) {
        currentRun = [
          seat,
        ];
        continue;
      }

      const previousSeat = currentRun[currentRun.length - 1];
      const previousSeatNumber = this.parseSeatNumber(previousSeat.seat);
      if (seatNumber === null || previousSeatNumber === null) {
        flushRun();
        currentRun = [
          seat,
        ];
        flushRun();
      } else if (seatNumber === previousSeatNumber + 1) {
        currentRun.push(seat);
      } else {
        flushRun();
        currentRun = [
          seat,
        ];
      }
    }

    flushRun();
    return runs;
  }

  private parseSeatNumber(seat: string): number | null {
    const seatNumber = Number(seat);
    return Number.isInteger(seatNumber) ? seatNumber : null;
  }

  private compareNatural(firstValue: string, secondValue: string): number {
    return this.naturalCollator.compare(firstValue, secondValue);
  }

  private compareTicketByField(left: EventTicket, right: EventTicket, field: TicketSortField): number {
    if (field === 'code') {
      return this.compareNatural(left.code, right.code);
    }
    if (field === 'sector') {
      return this.compareNatural(left.sector, right.sector);
    }
    if (field === 'row') {
      return this.compareNatural(left.row, right.row);
    }
    if (field === 'seat') {
      return this.compareNatural(left.seat, right.seat);
    }
    if (field === 'purchase') {
      return left.purchasePrice - right.purchasePrice;
    }
    if (field === 'sale') {
      return left.salePrice - right.salePrice;
    }
    if (field === 'payer') {
      return this.compareNatural(
        this.getPurchasePayerName(left.purchasePaidByUserId),
        this.getPurchasePayerName(right.purchasePaidByUserId),
      );
    }

    return this.getTicketStatusPriority(left.status) - this.getTicketStatusPriority(right.status);
  }

  private compareTicketPosition(left: EventTicket, right: EventTicket): number {
    return this.compareNatural(left.sector, right.sector)
      || this.compareNatural(left.row, right.row)
      || this.compareNatural(left.seat, right.seat);
  }

  private getTicketStatusPriority(status: TicketStatus): number {
    if (status === 'available') {
      return 0;
    }
    if (status === 'reserved') {
      return 1;
    }

    return 2;
  }

  private countTicketsByStatus(status: TicketStatus): number {
    if (!this.event) {
      return 0;
    }

    return this.event.tickets.filter((ticket) => ticket.status === status).length;
  }

  private stripTime(date: Date): Date {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    return normalizedDate;
  }

  private setEvent(event: SportsEvent): void {
    this.event = event;
    const defaultPayerId = this.getDefaultPurchasePayerId(event);
    if (!this.isEventParticipant(this.ticketForm.purchasePaidByUserId)) {
      this.ticketForm.purchasePaidByUserId = defaultPayerId;
    }
    if (!this.isEventParticipant(this.bulkPurchasePaidByUserId)) {
      this.bulkPurchasePaidByUserId = defaultPayerId;
    }
    this.ticketPriceDrafts = event.tickets.reduce<Record<string, TicketPriceDraft>>((drafts, ticket) => {
      drafts[ticket.id] = {
        purchasePrice: ticket.purchasePrice,
        salePrice: ticket.salePrice,
        purchasePaidByUserId: ticket.purchasePaidByUserId,
      };
      return drafts;
    }, {});
    this.selectedTicketIds = this.selectedTicketIds.filter((ticketId) => event.tickets.some((ticket) => ticket.id === ticketId && this.canEditTicketSalePrice(ticket)));
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private getDefaultPurchasePayerId(event: SportsEvent | null = this.event): string | null {
    return event?.participants[0]?.userId ?? null;
  }

  private normalizePurchasePayer(value: string | null | undefined): string | null {
    return value?.trim() || null;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
